import { useDrag } from 'solid-gesture'
import { type Component, createEffect, createMemo, onCleanup, Show } from 'solid-js'
import { Portal } from 'solid-js/web'
import { twMerge } from 'tailwind-merge'
import { usePresence } from '~/hooks/usePresence'
import type { DrawerPropsType } from '~/types/Drawer'
import { lockScroll, unlockScroll } from '~/utils/dom'
import { getGsap, MOTION, prefersReducedMotion } from '~/utils/gsap'
import Button from './Button'

const Drawer: Component<DrawerPropsType> = props => {
  const placement = () => props.position ?? 'right'
  const enableGestures = () => props.enableGestures !== false

  let panelRef: HTMLDivElement | undefined
  let backdropRef: HTMLDivElement | undefined
  let _dragging = false

  // Bloqueo de scroll balanceado por instancia: garantiza que cada lock tenga su
  // unlock incluso si el Drawer se desmonta abierto (p. ej. al navegar con el menú
  // móvil abierto). Sin esto, el <html> se quedaría con overflow:hidden y la página
  // no volvería a hacer scroll.
  let didLock = false
  const acquireLock = () => {
    if (!didLock) {
      didLock = true
      lockScroll()
    }
  }
  const releaseLock = () => {
    if (didLock) {
      didLock = false
      unlockScroll()
    }
  }

  const sizePx = () => {
    const sizes = { xs: 320, sm: 384, md: 448, lg: 512, xl: 576 } as const
    const k: keyof typeof sizes = props.size ?? 'md'
    return sizes[k]
  }

  const panelStyle = createMemo(() => {
    const p = placement()
    const px = sizePx()
    if (p === 'left' || p === 'right') return { width: `min(86vw, ${px}px)`, height: '100dvh' } as const
    return { height: `min(80vh, ${px}px)`, width: '100%' } as const
  })

  // Posición "cerrada" (fuera de pantalla) según el lado.
  const offscreen = () => {
    const p = placement()
    if (p === 'right') return { xPercent: 100, yPercent: 0 }
    if (p === 'left') return { xPercent: -100, yPercent: 0 }
    if (p === 'top') return { xPercent: 0, yPercent: -100 }
    return { xPercent: 0, yPercent: 100 } // bottom
  }

  const presence = usePresence({
    open: () => !!props.isOpen,
    enter: el => {
      const g = getGsap()
      if (!g) return
      const tl = g.timeline()
      if (backdropRef) tl.fromTo(backdropRef, { opacity: 0 }, { opacity: 1, duration: 0.2, ease: 'power2.out' }, 0)
      tl.to(el, { xPercent: 0, yPercent: 0, duration: MOTION.sheet.enter, ease: MOTION.sheet.ease }, 0)
      return tl
    },
    exit: el => {
      const g = getGsap()
      if (!g) return
      const tl = g.timeline()
      if (backdropRef) tl.to(backdropRef, { opacity: 0, duration: MOTION.sheet.exit, ease: 'power2.in' }, 0)
      tl.to(el, { ...offscreen(), duration: MOTION.sheet.exit, ease: 'power3.in' }, 0)
      return tl
    },
    onEntered: () => {
      // Movimiento reducido: garantizar estado visible/posicionado.
      if (backdropRef) backdropRef.style.opacity = ''
    },
    onExited: () => releaseLock()
  })

  createEffect(() => {
    if (props.isOpen) acquireLock()
  })

  // Si el Drawer se desmonta (navegación) estando abierto, libera el bloqueo igual.
  onCleanup(() => releaseLock())

  const handleClose = () => props.onClose()

  const getSizeInPixels = () => sizePx()

  const bindDrag = useDrag(
    (state: { movement: [number, number]; velocity: [number, number]; down: boolean }) => {
      if (!enableGestures() || !props.isOpen || !panelRef) return
      const g = getGsap()
      const p = placement()

      if (state.down) {
        _dragging = true
        // Seguir el dedo escribiendo el transform directamente (sin tween).
        if (p === 'right' && state.movement[0] < 0) g?.set(panelRef, { x: state.movement[0], y: 0 })
        else if (p === 'left' && state.movement[0] > 0) g?.set(panelRef, { x: state.movement[0], y: 0 })
        else if (p === 'top' && state.movement[1] < 0) g?.set(panelRef, { x: 0, y: state.movement[1] })
        else if (p === 'bottom' && state.movement[1] > 0) g?.set(panelRef, { x: 0, y: state.movement[1] })
        return
      }

      // Liberado: decidir cerrar o volver.
      _dragging = false
      const threshold = getSizeInPixels() * 0.3
      const v = state.velocity
      let shouldClose = false
      if (p === 'right') shouldClose = Math.abs(state.movement[0]) > threshold || v[0] < -0.5
      else if (p === 'left') shouldClose = Math.abs(state.movement[0]) > threshold || v[0] > 0.5
      else if (p === 'top') shouldClose = Math.abs(state.movement[1]) > threshold || v[1] < -0.5
      else if (p === 'bottom') shouldClose = Math.abs(state.movement[1]) > threshold || v[1] > 0.5

      if (shouldClose) {
        // El tween de salida de usePresence continúa desde el transform actual.
        handleClose()
      } else if (g) {
        g.to(panelRef, { x: 0, y: 0, duration: 0.15, ease: 'power2.out' })
      }
    },
    {
      filterTaps: true,
      axis: placement() === 'top' || placement() === 'bottom' ? ('y' as const) : ('x' as const)
    }
  )

  return (
    <Show when={presence.mounted()}>
      <Portal>
        <div class="fixed inset-0 z-50 h-dvh">
          {/* Backdrop — opacidad controlada por GSAP */}
          <div ref={backdropRef} class="absolute inset-0 bg-slate-900/50" style={{ opacity: 0 }} onClick={handleClose} data-testid="drawer-backdrop" />

          {/* Drawer panel */}
          <div
            onClick={handleClose}
            class={twMerge(
              'absolute flex h-full',
              placement() === 'right' || placement() === 'left' ? 'inset-y-0 max-w-full' : 'inset-x-0 w-full max-h-full',
              placement() === 'right' ? 'right-0' : '',
              placement() === 'left' ? 'left-0' : '',
              placement() === 'top' ? 'top-0 items-start' : '',
              placement() === 'bottom' ? 'bottom-0 items-end' : ''
            )}
          >
            <div class={'relative h-full'} style={panelStyle()}>
              <div
                ref={el => {
                  panelRef = el
                  presence.setRef(el)
                  // Coloca el panel fuera de pantalla antes del primer paint (evita parpadeo).
                  const g = getGsap()
                  if (g && !prefersReducedMotion()) g.set(el, offscreen())
                }}
                {...(enableGestures() ? bindDrag() : {})}
                onClick={e => e.stopPropagation()}
                style={{
                  'touch-action': placement() === 'top' || placement() === 'bottom' ? 'pan-y' : 'pan-x'
                }}
                class={twMerge('drawer-panel h-full max-h-dvh grid grid-rows-[auto_1fr] bg-surface shadow-xl', props.class)}
              >
                {/* Header */}
                <Show when={props.title || props.headerContent}>
                  <div class="bg-surface border-b border-border">
                    <div class="flex items-center justify-between p-4">
                      <div class="flex items-center gap-4">{props.headerContent || <h2 class="text-lg font-medium text-foreground">{props.title}</h2>}</div>
                      <Button icon="material-symbols-light:close" size="sm" iconSize={20} variant="text" color="default" onClick={handleClose} />
                    </div>
                  </div>
                </Show>

                {/* Content */}
                <div class="relative overflow-y-auto">{props.children}</div>
              </div>
            </div>
          </div>
        </div>
      </Portal>
    </Show>
  )
}

export default Drawer
